import type {
  SiteBuilderBlock,
  SiteBuilderBlockDefinition,
  SiteBuilderDropLine,
  SiteBuilderRichTextMark,
  SiteBuilderRichTextSegment,
  SiteBuilderZoneKind,
} from "@webdo/site-templates/builder";
import {
  advancedSlashCommandCandidateScore,
} from "./advanced/slashCommands";
import type {
  BuilderStudioBlockCatalog,
  BuilderStudioInteractionCapabilities,
  BuilderStudioLabels,
  BuilderStudioPlacementPolicy,
  BuilderStudioSlashCommandDefinition,
} from "./types";

export const DEFAULT_BUILDER_STUDIO_INTERACTIONS:
Readonly<BuilderStudioInteractionCapabilities> = {
  inlineEditing: true,
  nestedSlots: true,
  resize: true,
  slashCommands: true,
  styleClipboard: true,
};

export type BuilderStudioSlashCommand = {
  aliases: readonly string[];
  badge: string;
  columnCount?: 1 | 2 | 3 | 4;
  description: string;
  headingLevel?: 1 | 2 | 3;
  id: string;
  label: string;
  pattern?: string;
  score: number;
  type: string;
};

export function resolveBuilderStudioInteractions(
  overrides:
  Partial<BuilderStudioInteractionCapabilities> | null | undefined,
): BuilderStudioInteractionCapabilities {
  return {
    ...DEFAULT_BUILDER_STUDIO_INTERACTIONS,
    ...overrides,
  };
}

export function builderStudioAllowsRootBlock(
  policy: BuilderStudioPlacementPolicy | null | undefined,
  blockType: string,
): boolean {
  return !policy || policy.allowedRootBlockTypes.includes(blockType);
}

export function builderStudioAllowsNestedBlock(
  policy: BuilderStudioPlacementPolicy | null | undefined,
  blockType: string,
): boolean {
  return !policy?.rootOnlyBlockTypes?.includes(blockType);
}

export function builderStudioProtectsSystemRoot(
  definition: SiteBuilderBlockDefinition | null | undefined,
  addressDepth: number,
): boolean {
  return (
    addressDepth === 1
    && definition?.systemOnly === true
    && definition.insertable === false
  );
}

export function builderStudioOrderedZoneKeys(
  definition: SiteBuilderBlockDefinition | null | undefined,
  block: SiteBuilderBlock,
  policy: BuilderStudioPlacementPolicy | null | undefined,
): string[] {
  const zones = definition?.zones ?? [];
  const preferred = policy?.preferredSlotKeys ?? [];

  return [...zones]
    .sort((left, right) => {
      const leftPopulated = (block.slots?.[left.key]?.length ?? 0) > 0;
      const rightPopulated = (block.slots?.[right.key]?.length ?? 0) > 0;

      if (leftPopulated !== rightPopulated) {
        return leftPopulated ? -1 : 1;
      }

      const leftPreference = preferred.indexOf(left.key);
      const rightPreference = preferred.indexOf(right.key);
      const normalizedLeft = leftPreference < 0
        ? Number.MAX_SAFE_INTEGER
        : leftPreference;
      const normalizedRight = rightPreference < 0
        ? Number.MAX_SAFE_INTEGER
        : rightPreference;

      return normalizedLeft - normalizedRight;
    })
    .map((zone) => zone.key);
}

function richTextMarksEqual(
  left: SiteBuilderRichTextMark | undefined,
  right: SiteBuilderRichTextMark | undefined,
): boolean {
  const keys = new Set([
    ...Object.keys(left ?? {}),
    ...Object.keys(right ?? {}),
  ]);

  return [...keys].every(
    (key) =>
      left?.[key as keyof SiteBuilderRichTextMark]
      === right?.[key as keyof SiteBuilderRichTextMark],
  );
}

function richTextSegmentsSlice(
  segments: readonly SiteBuilderRichTextSegment[],
  start: number,
  end: number,
): SiteBuilderRichTextSegment[] {
  const result: SiteBuilderRichTextSegment[] = [];
  let offset = 0;

  for (const segment of segments) {
    const segmentStart = offset;
    const segmentEnd = offset + segment.text.length;
    const sliceStart = Math.max(start, segmentStart);
    const sliceEnd = Math.min(end, segmentEnd);

    if (sliceStart < sliceEnd) {
      result.push({
        ...(segment.marks ? { marks: { ...segment.marks } } : {}),
        text: segment.text.slice(
          sliceStart - segmentStart,
          sliceEnd - segmentStart,
        ),
      });
    }

    offset = segmentEnd;
  }

  return result;
}

function coalesceRichTextSegments(
  segments: readonly SiteBuilderRichTextSegment[],
): SiteBuilderRichTextSegment[] {
  const result: SiteBuilderRichTextSegment[] = [];

  for (const segment of segments) {
    if (segment.text === "") {
      continue;
    }

    const previous = result.at(-1);

    if (previous && richTextMarksEqual(previous.marks, segment.marks)) {
      previous.text += segment.text;
      continue;
    }

    result.push({
      ...(segment.marks ? { marks: { ...segment.marks } } : {}),
      text: segment.text,
    });
  }

  return result;
}

export type BuilderStudioTextRange = {
  end: number;
  start: number;
};

export function normalizeBuilderStudioTextRange(
  range: BuilderStudioTextRange | null | undefined,
  textLength: number,
): BuilderStudioTextRange {
  const limit = Math.max(0, Math.trunc(textLength));
  const rawStart = Math.max(
    0,
    Math.min(limit, Math.trunc(range?.start ?? 0)),
  );
  const rawEnd = Math.max(
    0,
    Math.min(limit, Math.trunc(range?.end ?? limit)),
  );

  return {
    end: Math.max(rawStart, rawEnd),
    start: Math.min(rawStart, rawEnd),
  };
}

function withRichTextMarks(
  marks: SiteBuilderRichTextMark | undefined,
  values: Readonly<Record<string, unknown>>,
): SiteBuilderRichTextMark | undefined {
  const next = {
    ...marks,
  };

  const keys: Array<keyof SiteBuilderRichTextMark> = [
    "fontFamily",
    "fontSize",
    "fontStyle",
    "fontWeight",
    "lineHeight",
    "textAlign",
    "textColor",
    "textDecoration",
  ];

  keys.forEach((key) => {
    if (!(key in values)) {
      return;
    }

    const value = values[key];

    if (value === undefined || value === "") {
      delete next[key];
    } else if (typeof value === "string") {
      next[key] = value;
    }
  });

  return Object.keys(next).length > 0 ? next : undefined;
}

/**
 * Applies marks to exactly the selected plain-text range. Segment boundaries
 * are split at both range edges and adjacent equal runs are compacted again.
 */
export function withBuilderStudioRichTextRangeMarks(
  segments: readonly SiteBuilderRichTextSegment[],
  range: BuilderStudioTextRange | null | undefined,
  values: Readonly<Record<string, unknown>>,
): SiteBuilderRichTextSegment[] {
  const textLength = segments.reduce(
    (length, segment) => length + segment.text.length,
    0,
  );
  const normalizedRange = normalizeBuilderStudioTextRange(range, textLength);

  if (normalizedRange.start === normalizedRange.end) {
    return segments.map((segment) => ({
      ...(segment.marks ? { marks: { ...segment.marks } } : {}),
      text: segment.text,
    }));
  }

  const selected = richTextSegmentsSlice(
    segments,
    normalizedRange.start,
    normalizedRange.end,
  ).map((segment) => {
    const marks = withRichTextMarks(segment.marks, values);

    return {
      ...(marks ? { marks } : {}),
      text: segment.text,
    };
  });

  return coalesceRichTextSegments([
    ...richTextSegmentsSlice(segments, 0, normalizedRange.start),
    ...selected,
    ...richTextSegmentsSlice(
      segments,
      normalizedRange.end,
      textLength,
    ),
  ]);
}

export function builderStudioRichTextMarksForRange(
  segments: readonly SiteBuilderRichTextSegment[],
  range: BuilderStudioTextRange | null | undefined,
): {
  marks: SiteBuilderRichTextMark;
  mixed: Array<keyof SiteBuilderRichTextMark>;
} {
  const textLength = segments.reduce(
    (length, segment) => length + segment.text.length,
    0,
  );
  const normalizedRange = normalizeBuilderStudioTextRange(range, textLength);
  const selected = richTextSegmentsSlice(
    segments,
    normalizedRange.start,
    normalizedRange.end,
  );
  const markKeys = new Set<keyof SiteBuilderRichTextMark>(
    selected.flatMap((segment) => (
      Object.keys(segment.marks ?? {}) as Array<keyof SiteBuilderRichTextMark>
    )),
  );
  const marks: SiteBuilderRichTextMark = {};
  const mixed: Array<keyof SiteBuilderRichTextMark> = [];

  markKeys.forEach((key) => {
    const values = new Set(selected.map((segment) => segment.marks?.[key]));

    if (values.size === 1) {
      const value = selected[0]?.marks?.[key];

      if (value !== undefined) {
        marks[key] = value;
      }
    } else {
      mixed.push(key);
    }
  });

  return {
    marks,
    mixed,
  };
}

export function builderStudioSlashCommands(
  catalog: BuilderStudioBlockCatalog,
  labels: BuilderStudioLabels,
  query: string,
  options: {
    commands?: readonly BuilderStudioSlashCommandDefinition[];
    isAvailable?: (
      command: BuilderStudioSlashCommandDefinition,
    ) => boolean;
  } = {},
): BuilderStudioSlashCommand[] {
  const commands = options.commands ?? catalog.insertableBlockTypes.map(
    (type): BuilderStudioSlashCommandDefinition => ({
      aliases: [type],
      id: type,
      type,
    }),
  );

  return commands.flatMap((command, index) => {
    if (options.isAvailable && !options.isAvailable(command)) {
      return [];
    }

    const label = command.label ?? labels.blockLabel(command.type);
    const description = command.description
      ?? labels.advanced?.slashCommandDescription?.(command.type)
      ?? labels.categoryLabel(
        (() => {
          try {
            return catalog.getBlockDefinition(command.type).category;
          } catch {
            return command.type;
          }
        })(),
      );
    const aliases = [
      command.type,
      command.id,
      ...command.aliases,
      label,
    ];
    const score = advancedSlashCommandCandidateScore(
      {
        aliases,
        id: command.id,
        type: command.type,
      },
      query,
      label,
      description,
    );

    return score === null
      ? []
      : [{
          aliases,
          badge: command.badge ?? String(index + 1),
          ...(command.columnCount
            ? { columnCount: command.columnCount }
            : {}),
          description,
          ...(command.headingLevel
            ? { headingLevel: command.headingLevel }
            : {}),
          id: command.id,
          label,
          ...(command.pattern ? { pattern: command.pattern } : {}),
          score,
          type: command.type,
        }];
  }).sort((left, right) => (
    left.score - right.score
    || left.label.localeCompare(right.label)
  )).slice(0, 8);
}

export function builderStudioZoneDefinition(
  definition: SiteBuilderBlockDefinition | null | undefined,
  slotKey: string,
) {
  return definition?.zones?.find((zone) => zone.key === slotKey) ?? null;
}

export function builderStudioZoneAccepts(
  definition: SiteBuilderBlockDefinition | null | undefined,
  slotKey: string,
  blockType: string,
  currentSize: number,
): boolean {
  const zone = builderStudioZoneDefinition(definition, slotKey);

  return Boolean(zone)
    && zone?.kind !== "fixed"
    && (zone?.accepts === undefined || zone.accepts.includes(blockType))
    && (zone?.capacity === undefined || currentSize < zone.capacity);
}

export function builderStudioZoneAllowsDropLine(
  definition: SiteBuilderBlockDefinition | null | undefined,
  slotKey: string,
  line: SiteBuilderDropLine,
): boolean {
  const zone = builderStudioZoneDefinition(definition, slotKey);

  return Boolean(zone)
    && zone?.kind !== "fixed"
    && (zone?.dropLines === undefined || zone.dropLines.includes(line));
}

export function builderStudioZoneKind(
  definition: SiteBuilderBlockDefinition | null | undefined,
  slotKey: string,
): SiteBuilderZoneKind | null {
  return builderStudioZoneDefinition(definition, slotKey)?.kind ?? null;
}

export function builderStudioPreferredDropLine({
  allowedLines,
  clientX,
  clientY,
  rect,
}: {
  allowedLines: readonly SiteBuilderDropLine[];
  clientX: number;
  clientY: number;
  rect: {
    height: number;
    left: number;
    top: number;
    width: number;
  };
}): SiteBuilderDropLine | null {
  if (allowedLines.length === 0) {
    return null;
  }

  const xRatio = rect.width > 0
    ? (clientX - rect.left) / rect.width
    : 0.5;
  const yRatio = rect.height > 0
    ? (clientY - rect.top) / rect.height
    : 0.5;

  if (xRatio <= 0.3 && allowedLines.includes("left")) {
    return "left";
  }

  if (xRatio >= 0.7 && allowedLines.includes("right")) {
    return "right";
  }

  if (yRatio <= 0.25 && allowedLines.includes("before")) {
    return "before";
  }

  if (yRatio >= 0.75 && allowedLines.includes("after")) {
    return "after";
  }

  if (allowedLines.includes("below")) {
    return "below";
  }

  if (allowedLines.includes("inside")) {
    return "inside";
  }

  return allowedLines[0] ?? null;
}

export function builderStudioInsertIndexForDropLine(
  anchorIndex: number,
  line: SiteBuilderDropLine,
): number {
  return line === "before" || line === "left"
    ? anchorIndex
    : anchorIndex + 1;
}

export type BuilderStudioGridPlacement = {
  h: number;
  w: number;
  x: number;
  y: number;
};

function finiteGridValue(
  value: unknown,
  fallback: number,
): number {
  const numeric = Number(value);

  return Number.isFinite(numeric)
    ? Math.max(1, Math.round(numeric))
    : fallback;
}

export function builderStudioGridPlacementForPoint({
  block,
  clientX,
  clientY,
  columns,
  rect,
  rowHeight,
}: {
  block: SiteBuilderBlock;
  clientX: number;
  clientY: number;
  columns: number;
  rect: {
    height: number;
    left: number;
    top: number;
    width: number;
  };
  rowHeight: number;
}): BuilderStudioGridPlacement {
  const columnCount = Math.max(1, Math.round(columns));
  const w = Math.min(
    columnCount,
    finiteGridValue(block.options?.gridW, Math.min(6, columnCount)),
  );
  const h = finiteGridValue(block.options?.gridH, 2);
  const pointerColumn = Math.floor(
    ((clientX - rect.left) / Math.max(1, rect.width)) * columnCount,
  ) + 1;

  return {
    h,
    w,
    x: Math.max(1, Math.min(columnCount - w + 1, pointerColumn)),
    y: Math.max(
      1,
      Math.floor((clientY - rect.top) / Math.max(1, rowHeight)) + 1,
    ),
  };
}

export function builderStudioRelativeGridPlacement(
  anchor: SiteBuilderBlock,
  incoming: SiteBuilderBlock,
  line: SiteBuilderDropLine,
  columns: number,
): BuilderStudioGridPlacement {
  const columnCount = Math.max(1, Math.round(columns));
  const anchorPlacement = {
    h: finiteGridValue(anchor.options?.gridH, 2),
    w: Math.min(
      columnCount,
      finiteGridValue(anchor.options?.gridW, Math.min(6, columnCount)),
    ),
    x: finiteGridValue(anchor.options?.gridX, 1),
    y: finiteGridValue(anchor.options?.gridY, 1),
  };
  const incomingW = Math.min(
    columnCount,
    finiteGridValue(incoming.options?.gridW, Math.min(6, columnCount)),
  );
  const incomingH = finiteGridValue(incoming.options?.gridH, 2);
  const x = line === "left"
    ? anchorPlacement.x - incomingW
    : line === "right"
      ? anchorPlacement.x + anchorPlacement.w
      : anchorPlacement.x;
  const y = line === "below" || line === "after"
    ? anchorPlacement.y + anchorPlacement.h
    : anchorPlacement.y;

  return {
    h: incomingH,
    w: incomingW,
    x: Math.max(1, Math.min(columnCount - incomingW + 1, x)),
    y: Math.max(1, y),
  };
}

export function withBuilderStudioGridPlacement(
  block: SiteBuilderBlock,
  placement: BuilderStudioGridPlacement,
): SiteBuilderBlock {
  return {
    ...block,
    options: {
      ...block.options,
      gridH: placement.h,
      gridW: placement.w,
      gridX: placement.x,
      gridY: placement.y,
    },
  };
}

export function builderStudioStyleOptions(
  block: SiteBuilderBlock,
): Readonly<Record<string, unknown>> {
  return block.options ?? {};
}

export function withBuilderStudioStyleOptions(
  block: SiteBuilderBlock,
  values: Readonly<Record<string, unknown>>,
): SiteBuilderBlock {
  return {
    ...block,
    options: {
      ...block.options,
      ...values,
    },
  };
}

export function toggledBuilderStudioStyleValue(
  current: unknown,
  activeValue: string,
  inactiveValue?: string,
): string | undefined {
  return String(current ?? "") === activeValue
    ? inactiveValue
    : activeValue;
}

export function builderStudioResizeOptions({
  deltaX,
  deltaY,
  handle,
  height,
  width,
}: {
  deltaX: number;
  deltaY: number;
  handle: "e" | "n" | "s" | "w";
  height: number;
  width: number;
}): Record<string, string> {
  const nextWidth = handle === "e"
    ? width + deltaX
    : handle === "w"
      ? width - deltaX
      : width;
  const nextHeight = handle === "s"
    ? height + deltaY
    : handle === "n"
      ? height - deltaY
      : height;

  return {
    ...(handle === "e" || handle === "w"
      ? {
          width: `${Math.round(
            Math.max(24, Math.min(2400, nextWidth)),
          )}px`,
        }
      : {}),
    ...(handle === "n" || handle === "s"
      ? {
          minHeight: `${Math.round(
            Math.max(24, Math.min(2400, nextHeight)),
          )}px`,
        }
      : {}),
  };
}
